import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { BarChart3, TrendingUp, AlertTriangle, DollarSign } from "lucide-react";
import { projectId, publicAnonKey } from "../../utils/supabase/info";

interface UsageStats {
  currentMonth: {
    totalTokens: number;
    totalCost: number;
    requestCount: number;
    averageTokensPerRequest: number;
  };
  byCompany: {
    companyId: string;
    companyName: string;
    tokens: number;
    cost: number;
    requests: number;
  }[];
  dailyUsage: {
    date: string;
    tokens: number;
    cost: number;
  }[];
  alerts: {
    type: "warning" | "error";
    message: string;
  }[];
}

interface AIUsageMonitorProps {
  accessToken: string;
}

export function AIUsageMonitor({ accessToken }: AIUsageMonitorProps) {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthlyBudget] = useState(100); // $100 monthly budget

  useEffect(() => {
    fetchUsageStats();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchUsageStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchUsageStats = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/usage/stats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching AI usage stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Usage & Costs</CardTitle>
          <CardDescription>Loading usage statistics...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Usage & Costs</CardTitle>
          <CardDescription>No usage data available yet</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const budgetPercent = (stats.currentMonth.totalCost / monthlyBudget) * 100;
  const isNearBudget = budgetPercent > 80;
  const isOverBudget = budgetPercent > 100;

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {(isOverBudget || stats.alerts.length > 0) && (
        <div className="space-y-3">
          {isOverBudget && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Budget Exceeded</AlertTitle>
              <AlertDescription>
                Your AI usage has exceeded the monthly budget of ${monthlyBudget}.
                Current spending: ${stats.currentMonth.totalCost.toFixed(2)}
              </AlertDescription>
            </Alert>
          )}
          {isNearBudget && !isOverBudget && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Approaching Budget Limit</AlertTitle>
              <AlertDescription>
                You've used {budgetPercent.toFixed(0)}% of your monthly AI budget.
              </AlertDescription>
            </Alert>
          )}
          {stats.alerts.map((alert, index) => (
            <Alert key={index} variant={alert.type === "error" ? "destructive" : "default"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{alert.message}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Current Month Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Current Month Usage
          </CardTitle>
          <CardDescription>
            Token usage and costs for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Budget Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Monthly Budget</span>
              <span className={isOverBudget ? "text-destructive" : ""}>
                ${stats.currentMonth.totalCost.toFixed(2)} / ${monthlyBudget.toFixed(2)}
              </span>
            </div>
            <Progress 
              value={Math.min(budgetPercent, 100)} 
              className={isOverBudget ? "bg-destructive/20" : ""}
            />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Requests</p>
              <p className="text-2xl">{stats.currentMonth.requestCount.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Tokens</p>
              <p className="text-2xl">{stats.currentMonth.totalTokens.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Avg Tokens/Request</p>
              <p className="text-2xl">{Math.round(stats.currentMonth.averageTokensPerRequest)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Cost</p>
              <p className="text-2xl flex items-center gap-1">
                <DollarSign className="h-5 w-5" />
                {stats.currentMonth.totalCost.toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage by Company */}
      <Card>
        <CardHeader>
          <CardTitle>Usage by Company</CardTitle>
          <CardDescription>Token usage breakdown per company</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.byCompany.length === 0 ? (
              <p className="text-sm text-muted-foreground">No company usage yet</p>
            ) : (
              stats.byCompany.map((company) => (
                <div key={company.companyId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{company.companyName}</p>
                      <p className="text-sm text-muted-foreground">
                        {company.requests} requests · {company.tokens.toLocaleString()} tokens
                      </p>
                    </div>
                    <Badge variant="outline">${company.cost.toFixed(2)}</Badge>
                  </div>
                  <Progress 
                    value={(company.tokens / stats.currentMonth.totalTokens) * 100} 
                  />
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Daily Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Daily Usage Trend
          </CardTitle>
          <CardDescription>Last 7 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats.dailyUsage.slice(-7).map((day) => (
              <div key={day.date} className="flex items-center justify-between text-sm">
                <span>{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <div className="flex items-center gap-4">
                  <span className="text-muted-foreground">{day.tokens.toLocaleString()} tokens</span>
                  <Badge variant="outline">${day.cost.toFixed(2)}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cost Estimates */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Information</CardTitle>
          <CardDescription>OpenAI GPT-4 pricing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Input tokens:</span>
            <span>$0.03 / 1K tokens</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Output tokens:</span>
            <span>$0.06 / 1K tokens</span>
          </div>
          <div className="flex justify-between pt-2 border-t">
            <span className="text-muted-foreground">Projected monthly cost:</span>
            <span className={isOverBudget ? "text-destructive" : ""}>
              ${(stats.currentMonth.totalCost * (30 / new Date().getDate())).toFixed(2)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
